/**
 * SettingsSidebar — Frame.io-style vertical nav for the settings tree.
 *
 * Sections are role-filtered: the Admin section is hidden entirely for
 * non-ADMIN users (locked 2026-07-21 — see project_workspace_settings_spec).
 *
 * Badges ("New", "Beta", "Coming soon") are display-only.
 */

import { NavLink } from "react-router-dom";
import {
  Bell,
  BookOpen,
  CreditCard,
  FileText,
  FlaskConical,
  FolderKanban,
  HardDrive,
  PieChart,
  Shield,
  Sparkles,
  Type,
  UserCircle2,
  Users as UsersIcon,
  Webhook,
  Zap,
  type LucideIcon,
} from "lucide-react";

type Item = {
  key: string;
  label: string;
  icon: LucideIcon;
  badge?: "New" | "Beta" | "Soon";
};

type Section = {
  title: string;
  items: Item[];
  adminOnly?: boolean;
};

export const SECTIONS: Section[] = [
  {
    title: "Personal",
    items: [
      { key: "profile", label: "Profile", icon: UserCircle2 },
      { key: "notifications", label: "Notifications", icon: Bell, badge: "Soon" },
    ],
  },
  {
    title: "Account",
    items: [
      { key: "usage", label: "Usage", icon: PieChart },
      { key: "storage", label: "Storage", icon: HardDrive },
      { key: "plan", label: "Plan", icon: BookOpen, badge: "Soon" },
      { key: "billing", label: "Billing", icon: CreditCard, badge: "Soon" },
      { key: "branding", label: "Branding", icon: Sparkles, badge: "Soon" },
      { key: "labs", label: "Labs", icon: FlaskConical, badge: "Soon" },
    ],
  },
  {
    title: "Admin",
    adminOnly: true,
    items: [
      { key: "users", label: "Users", icon: UsersIcon },
      { key: "projects", label: "Projects", icon: FolderKanban },
      { key: "fields", label: "Fields", icon: Type, badge: "Soon" },
      { key: "content-security", label: "Content Security", icon: Shield, badge: "Soon" },
    ],
  },
  {
    title: "Integrations",
    items: [
      { key: "actions", label: "Actions", icon: Zap, badge: "Soon" },
      { key: "webhooks", label: "Webhooks", icon: Webhook, badge: "Soon" },
    ],
  },
  {
    title: "",
    items: [{ key: "legal", label: "Legal Notices", icon: FileText, badge: "Soon" }],
  },
];

type Props = {
  basePath: "/app/settings" | "/admin/settings";
  role: string | null | undefined;
};

export function SettingsSidebar({ basePath, role }: Props) {
  const isAdmin = role === "ADMIN";
  const visibleSections = SECTIONS.filter((s) => !s.adminOnly || isAdmin);

  return (
    // h-full fills the SettingsShell's bounded height; overflow-y-auto gives
    // this column its OWN scrollbar so long section lists don't drag the
    // content area. Sidebar is never scrolled off-screen because it always
    // occupies the full column height. See settings-shell.tsx for the reason
    // we can't use `sticky` here (main has overflow-hidden, which broke it).
    <nav
      aria-label="Settings navigation"
      className="h-full w-64 shrink-0 overflow-y-auto border-r border-border/60 bg-sidebar px-3 py-6"
    >
      <div className="flex flex-col gap-6">
        {visibleSections.map((section, idx) => (
          <div key={section.title || `unnamed-${idx}`}>
            {section.title && (
              <div className="mb-2 px-3 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                {section.title}
              </div>
            )}
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <SidebarItem key={item.key} basePath={basePath} item={item} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

function SidebarItem({ basePath, item }: { basePath: string; item: Item }) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={`${basePath}/${item.key}`}
        className={({ isActive }) =>
          [
            "group flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
            isActive
              ? "bg-secondary text-foreground"
              : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
          ].join(" ")
        }
      >
        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
        <span className="truncate">{item.label}</span>
        {item.badge && <BadgeChip label={item.badge} />}
      </NavLink>
    </li>
  );
}

function BadgeChip({ label }: { label: "New" | "Beta" | "Soon" }) {
  const styles: Record<typeof label, string> = {
    New: "border-primary/40 bg-primary/10 text-primary",
    Beta: "border-emerald-400/40 bg-emerald-400/10 text-emerald-500",
    Soon: "border-muted-foreground/30 bg-muted-foreground/10 text-muted-foreground",
  };
  return (
    <span
      className={
        "ml-auto rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none " +
        styles[label]
      }
    >
      {label}
    </span>
  );
}
